/**
 * Amplitude `Identify` helper for PostHog feature flag user properties.
 *
 * Called from `PosthogProvider`'s `onFeatureFlags` callback so that every
 * Amplitude event in the session can be sliced by flag cohort without those
 * events needing to carry flag context themselves.
 *
 * F1 scope: `isSaasTelemetryEnabled` is a build-time-folded `const boolean`,
 * so `if (!isSaasTelemetryEnabled) return;` lets DCE drop every Amplitude call
 * below — no `Identify`/`identify` ever executes on a suppressed build. See
 * .ai-context/onprem-guardrails.md (rule F1).
 */

import { shouldSyncFlagToAmplitude } from '@/providers/amplitude-flag-sync-policy';
import { isSaasTelemetryEnabled } from '@/utils/buildProfile';
import * as amplitude from '@amplitude/analytics-browser';

/**
 * Send all PostHog feature flags as Amplitude user properties.
 *
 * Each flag name is prefixed with `flag_` to avoid collisions with other user
 * properties. Boolean flags are set as `true`/`false`; multivariate flags use
 * their variant string value.
 *
 * @param variants - The full flag→value map from posthog.featureFlags.getFlagVariants().
 *   Typed as a record by the SDK, but absent at runtime whenever the flag fetch
 *   never landed — a cold boot with no connectivity — so it is accepted as
 *   optional here rather than being let through to `Object.entries`.
 */
export function setFeatureFlagUserProperties(
  variants: Record<string, string | boolean> | undefined
): void {
  // TELEMETRY-GATE: F1 function-entry gate. Mirrors the canonical pattern in
  // src/providers/amplitude-provider.tsx — Amplitude must not be driven via
  // .identify() when telemetry is suppressed.
  if (!isSaasTelemetryEnabled) return;

  if (!variants) return;

  try {
    const identify = new amplitude.Identify();
    for (const [flagKey, flagValue] of Object.entries(variants)) {
      if (!shouldSyncFlagToAmplitude(flagKey)) continue;
      identify.set(`flag-${flagKey}`, flagValue);
    }
    amplitude.identify(identify);
  } catch {
    // Amplitude unavailable (SSR, blocked by adblock, etc.) — non-fatal.
  }
}
